import React from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import {
  Route as RNTVRoute,
  TabView as RNTVTabView,
  TabViewProps as RNTVTabViewProps,
  TabBar,
  TabBarItem,
  TabBarItemProps,
  TabBarProps,
} from 'react-native-tab-view';

import Icon from '@components/Icon';
import { useAppTheme } from '@context/ThemeContext';

export type Route = Omit<RNTVRoute, 'title' | 'icon'> & {
  title: string;
  icon?: React.ComponentProps<typeof Icon>['name'];
};

export enum TabBarVariant {
  DEFAULT = 'DEFAULT',
  SMALL = 'SMALL',
}

export type TabViewProps<T extends Route> = Omit<
  RNTVTabViewProps<T>,
  'navigationState' | 'onIndexChange'
> & {
  routes: T[];
  initiallyTrackRouteKey?: T['key'];
  onIndexChange?: (index: number) => void;
  tabBarVariant?: TabBarVariant;
  tabBarScrollEnabled?: boolean;
};

export function TabView<T extends Route>({
  routes,
  initiallyTrackRouteKey,
  onIndexChange,
  tabBarVariant,
  tabBarScrollEnabled,
  ...props
}: TabViewProps<T>) {
  const initialRouteIndex = initiallyTrackRouteKey
    ? getRouteIndex(routes, initiallyTrackRouteKey)
    : 0;

  const layout = useWindowDimensions();
  const trackedRouteIndex = initiallyTrackRouteKey
    ? getRouteIndex(routes, initiallyTrackRouteKey)
    : 0;
  const [indexState, setIndexState] = React.useState({
    index: initialRouteIndex,
    trackedRouteIndex,
    trackedRouteKey: initiallyTrackRouteKey,
  });
  const shouldUseTrackedIndex =
    initiallyTrackRouteKey &&
    (indexState.trackedRouteKey !== initiallyTrackRouteKey ||
      indexState.trackedRouteIndex !== trackedRouteIndex);
  const index = shouldUseTrackedIndex ? trackedRouteIndex : indexState.index;
  const appTheme = useAppTheme();

  const tabBarStyle = {
    backgroundColor: appTheme.colors.background,
  };

  const renderTabBar = (props: TabBarProps<T>) => (
    <TabBar
      {...props}
      renderTabBarItem={(props: TabBarItemProps<T>) => (
        <TabBarItem
          {...props}
          // `key` added manually to avoid warnings observed in testing
          // ref: https://github.com/react-navigation/react-navigation/issues/11989#issuecomment-2113658902
          key={props.route.key}
          icon={({ route, color, size }) =>
            route.icon ? <Icon name={route.icon} size={size} color={color} /> : null
          }
          labelStyle={[styles.tabLabel, props.labelStyle]}
          style={[styles.tabBarItem, props.style]}
        />
      )}
      scrollEnabled={tabBarScrollEnabled}
      indicatorStyle={{ backgroundColor: appTheme.colors.primary }}
      style={tabBarStyle}
      activeColor={appTheme.colors.primary}
      inactiveColor={appTheme.colors.placeholder}
      tabStyle={tabBarVariant === TabBarVariant.SMALL ? styles.tabSmall : {}}
    />
  );

  const handleIndexChange = (index: number) => {
    setIndexState({
      index,
      trackedRouteIndex,
      trackedRouteKey: initiallyTrackRouteKey,
    });
    onIndexChange?.(index);
  };

  return (
    <RNTVTabView
      {...props}
      navigationState={{ index, routes }}
      renderTabBar={renderTabBar}
      onIndexChange={handleIndexChange}
      initialLayout={{ width: layout.width }}
    />
  );
}

const getRouteIndex = (routes: Route[], key: Route['key']) => {
  const idx = routes.findIndex((val) => val.key === key);
  if (idx === -1) {
    return 0;
  }
  return idx;
};

const styles = StyleSheet.create({
  tabBarItem: {
    flexDirection: 'row',
    minHeight: 40,
    padding: 6,
  },
  tabLabel: {
    fontSize: 16,
    margin: 2,
  },
  tabSmall: {
    width: 'auto',
  },
});
